import { z } from 'zod';

// Validation Constants
const VALIDATION_LIMITS = {
  MIN_PASSWORD_LENGTH: 6,
  MAX_LIMIT: 100,
} as const;

export const ApiConfigSchema = z.object({
  baseUrl: z.string().url('Base URL must be a valid URL'),
  workspaceId: z.string().min(1, 'Workspace ID is required'),
  timeout: z.number().min(1).max(300000).optional(),
  headers: z.record(z.string(), z.string()).optional(),
});

export const LoginRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const RegisterRequestSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(
      VALIDATION_LIMITS.MIN_PASSWORD_LENGTH,
      'Password must be at least 6 characters',
    ),
});

export const FindRecordsParamsSchema = z.object({
  resourceName: z.string().min(1, 'Resource name is required'),
  search: z.string().optional(),
  page: z.number().min(1).optional(),
  limit: z.number().min(1).max(VALIDATION_LIMITS.MAX_LIMIT).optional(),
});

export const BaseEntitySchema = z.object({
  id: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const PaginationMetaSchema = z.object({
  activePageNumber: z.number(),
  limit: z.number(),
  totalRecords: z.number(),
  totalPages: z.number(),
  hasNext: z.boolean(),
  hasPrev: z.boolean(),
});

export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(
  recordSchema: T,
): z.ZodObject<{
  records: z.ZodArray<T>;
  meta: typeof PaginationMetaSchema;
}> =>
  z.object({
    records: z.array(recordSchema),
    meta: PaginationMetaSchema,
  });

export const AuthResponseSchema = z.object({
  access_token: z.string(),
  expires_in: z.number(),
  token_type: z.string(),
  user: z.object({
    id: z.string(),
    email: z.string().email(),
    name: z.string(),
  }),
});

// Additional schemas for backward compatibility
export const UserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
});

export const ApiErrorSchema = z.object({
  message: z.string(),
  errors: z
    .array(
      z.object({
        field: z.string(),
        message: z.string(),
      }),
    )
    .optional(),
});

export const LoggedUserSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().email(),
  settings: z.object({
    themeMode: z.enum(['light', 'dark']),
  }),
});

export const UpdateLoggedUserSchema = z.object({
  name: z.string().nullish(),
  themeMode: z.enum(['light', 'dark']).nullish(),
});

export type ApiConfig = z.infer<typeof ApiConfigSchema>;
export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;
export type FindRecordsParams = z.infer<typeof FindRecordsParamsSchema>;
export type BaseEntity = z.infer<typeof BaseEntitySchema>;
export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;
export type AuthResponse = z.infer<typeof AuthResponseSchema>;
export type User = z.infer<typeof UserSchema>;
export type ApiError = z.infer<typeof ApiErrorSchema>;
export type LoggedUser = z.infer<typeof LoggedUserSchema>;
export type UpdateLoggedUser = z.infer<typeof UpdateLoggedUserSchema>;

// Metrics API Schemas
export const FieldMetricsParamsSchema = z.object({
  resourceName: z.string().min(1, 'Resource name is required'),
  fields: z.array(z.string()).optional(),
});

// Distribution item for ENUM and BOOLEAN fields
export const DistributionItemSchema = z.object({
  value: z.string(),
  count: z.number(),
  percentage: z.number(),
});

// Year distribution item for DATE fields
export const YearDistributionItemSchema = z.object({
  year: z.number(),
  count: z.number(),
});

// Numeric field statistics (INTEGER, DECIMAL, MONEY, PERCENTAGE)
export const NumericFieldStatsSchema = z.object({
  sum: z.number(),
  average: z.number(),
  minimum: z.number(),
  maximum: z.number(),
  count: z.number(),
  totalRecords: z.number(),
  standardDeviation: z.number(),
  nullCount: z.number(),
});

// Enum field statistics
export const EnumFieldStatsSchema = z.object({
  distribution: z.array(DistributionItemSchema),
  totalRecords: z.number(),
  nonNullRecords: z.number(),
  nullCount: z.number(),
  uniqueValues: z.number(),
  mostCommon: z.string(),
});

// Boolean field statistics
export const BooleanFieldStatsSchema = z.object({
  distribution: z.array(DistributionItemSchema),
  trueCount: z.number(),
  falseCount: z.number(),
  nullCount: z.number(),
  totalRecords: z.number(),
  truePercentage: z.number(),
  falsePercentage: z.number(),
  nullPercentage: z.number(),
});

// Date field statistics (DATE, DATETIME)
export const DateFieldStatsSchema = z.object({
  earliest: z.string(),
  latest: z.string(),
  count: z.number(),
  totalRecords: z.number(),
  nullCount: z.number(),
  yearDistribution: z.array(YearDistributionItemSchema),
  mostActiveYear: z.number(),
});

// Generic field stats for unsupported field types
export const GenericFieldStatsSchema = z.object({
  message: z.string(),
});

// Field metric with field metadata
export const FieldMetricSchema = z.object({
  type: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  stats: z.union([
    NumericFieldStatsSchema,
    EnumFieldStatsSchema,
    BooleanFieldStatsSchema,
    DateFieldStatsSchema,
    GenericFieldStatsSchema,
  ]),
});

// Complete metrics response
export const MetricsResponseSchema = z.object({
  entity: z.string(),
  workspace: z.string(),
  metrics: z.record(z.string(), FieldMetricSchema),
});

export type FieldMetricsParams = z.infer<typeof FieldMetricsParamsSchema>;
export type DistributionItem = z.infer<typeof DistributionItemSchema>;
export type YearDistributionItem = z.infer<typeof YearDistributionItemSchema>;
export type NumericFieldStats = z.infer<typeof NumericFieldStatsSchema>;
export type EnumFieldStats = z.infer<typeof EnumFieldStatsSchema>;
export type BooleanFieldStats = z.infer<typeof BooleanFieldStatsSchema>;
export type DateFieldStats = z.infer<typeof DateFieldStatsSchema>;
export type GenericFieldStats = z.infer<typeof GenericFieldStatsSchema>;
export type FieldMetric = z.infer<typeof FieldMetricSchema>;
export type MetricsResponse = z.infer<typeof MetricsResponseSchema>;
